import { portalLocale, portalText } from "../../i18n/portal.js";
/**
 * `data-screen-label="Audit log"` — the words and glyphs the screen puts on a row (P14-06).
 *
 * Pure functions over the view types, so every sentence the screen can say is provable
 * without mounting anything.
 *
 * ── WHO DID IT ───────────────────────────────────────────────────────────────────────────
 * Three actors that must never be mistaken for each other (AC-33):
 *
 *   · a **member** — shown by name, as the prototype draws it;
 *   · a **WeTalk operator** — shown by the name the entry itself recorded
 *     (`detail.operator_display_name`, IC-13, which the API sends as `actor_name` and, when the
 *     operator was acting as a member, as `impersonated_by_operator_name` too). When the entry
 *     recorded no name the slice has already worded it "A WeTalk operator (name not recorded)";
 *     this module never supplies a name of its own and never falls back to the member's;
 *   · an **API key** — shown by the key's name with an "API key" tag, never as though a person
 *     had signed in and done it.
 *
 * Everything the view says about the actor is used as given. An API key is recognised by the
 * view's `origin` — the API's own value, carried since 2026-09-23 (register 0z.3) — and never by
 * the slice's wording of it in `fromText`, so rewording "API key" cannot turn a key into a person.
 * An operator's `actorName` IS the operator (IC-13), so an operator row is headed by it.
 */
import type { PhosphorIcon } from "@wetalk/ui";

import { GLYPH, UnknownGlyphError } from "../../shell/glyph.js";
import { AUDIT_FILTER } from "../../api/governance-view.js";
import type { AuditEntryRowView, AuditEventClassView, AuditResultView } from "../../api/gateway.js";

/**
 * Every glyph a row can ask for: the slice's prefix icons, its four class icons, and the
 * sandbox's worked example. Keyed by the Phosphor class name the prototype uses.
 */
const ROW_GLYPH: Readonly<Record<string, PhosphorIcon>> = {
  "ph-duotone ph-git-branch": GLYPH.gitBranch,
  "ph-duotone ph-credit-card": GLYPH.creditCard,
  "ph-duotone ph-shield-check": GLYPH.shieldCheck,
  "ph-duotone ph-users": GLYPH.people,
  "ph-duotone ph-headphones": GLYPH.headphones,
  "ph-duotone ph-user-switch": GLYPH.personSwitch,
  "ph-duotone ph-phone-outgoing": GLYPH.phoneOutgoing,
  "ph-duotone ph-key": GLYPH.key,
  "ph-duotone ph-user-plus": GLYPH.personPlus,
  "ph-duotone ph-trash": GLYPH.trash,
  "ph-duotone ph-arrows-clockwise": GLYPH.arrowsClockwise,
};

/** Resolves a row's icon. Raises on an unknown one — never substitutes another glyph. */
export function rowGlyphOf(iconName: string): PhosphorIcon {
  const found = ROW_GLYPH[iconName];
  if (found === undefined) throw new UnknownGlyphError(iconName);
  return found;
}

/** The value the segmented control carries for "Everything", which is no filter at all. */
export const EVERYTHING = "everything";
export type AuditFilterValue = AuditEventClassView | typeof EVERYTHING;

export const FILTER_OPTION: readonly { readonly value: AuditFilterValue; readonly label: string }[] =
  AUDIT_FILTER.map((one) => ({ value: one.value ?? EVERYTHING, get label() { return one.label; } }));

export function filterValueOf(eventClass: AuditEventClassView | null): AuditFilterValue {
  return eventClass ?? EVERYTHING;
}

export function eventClassOfFilter(value: AuditFilterValue): AuditEventClassView | null {
  return value === EVERYTHING ? null : value;
}

/** "Agents", "Outbound", "Billing", "Compliance", "People" — the filter's own label for a class. */
export function filterLabelOf(eventClass: AuditEventClassView): string {
  const found = AUDIT_FILTER.find((one) => one.value === eventClass);
  if (found === undefined) {
    throw new Error(portalText("finance", "m1087", { value1: eventClass }));
  }
  return found.label;
}

function countWord(count: number): string {
  return count.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"));
}

/**
 * "9 entries · every change by a person or an automation" — the design's note. AMENDED plan-014 C4b: past the API's
 * count ceiling it says "10,000+", and Finance is told the view holds financial entries only.
 */
export function entryNoteText(matchedCount: number, capped = false, financeOnly = false): string {
  const figure = capped ? `${countWord(matchedCount)}+` : countWord(matchedCount);
  const what = financeOnly ? portalText("finance", "m1088") : portalText("finance", "m1089");
  return portalText("finance", "audit.entries", { count: capped ? Math.max(2, matchedCount) : matchedCount, value1: figure, value2: what });
}

/** The kicker: "Roma Pizzeria · kept for 24 months". */
export function kickerText(workspaceName: string | null, retentionMonth: number | null): string {
  const kept = retentionMonth === null ? null : portalText("finance", "m1090", { value1: String(retentionMonth) });
  if (workspaceName === null) return kept ?? "";
  return kept === null ? workspaceName : `${workspaceName} · ${kept}`;
}

// ---- plan-014 C4b (FR12-SEC-08): the organization log, the filters, the result and the details.

/** Which log the screen reads. */
export type AuditEntryScopeChoice = "workspace" | "organization";

export const LOG_OPTION: readonly { readonly value: AuditEntryScopeChoice; readonly label: string }[] = [
  { value: "workspace", get label() { return portalText("finance", "m0165"); } },
  { value: "organization", get label() { return portalText("finance", "m1091"); } },
];

/** The organization log's kicker: "Roma Pizzeria · every workspace · kept for 24 months". */
export function organizationKickerText(accountName: string | null, retentionMonth: number | null): string {
  return kickerText(accountName === null ? portalText("finance", "m1092") : portalText("finance", "m1093", { value1: accountName }), retentionMonth);
}

/** A result worth saying: a success is the normal case and carries no tag. */
export const RESULT_TAG_TEXT: Readonly<Record<Exclude<AuditResultView, "succeeded">, string>> = {
  get refused() { return portalText("finance", "m1095"); },
  get failed() { return portalText("finance", "m1096"); },
};

export const RESULT_OPTION: readonly { readonly value: AuditResultView | "any"; readonly label: string }[] = [
  { value: "any", get label() { return portalText("finance", "m1094"); } },
  { value: "succeeded", get label() { return portalText("finance", "m0597"); } },
  { value: "refused", get label() { return portalText("finance", "m1095"); } },
  { value: "failed", get label() { return portalText("finance", "m1096"); } },
];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A date input's day as the instant it starts, in UTC (`from`, inclusive); `null` for an empty or partial input. */
export function dayStartOf(day: string): string | null {
  return DAY.test(day) ? `${day}T00:00:00Z` : null;
}

/** The instant the day AFTER a date input's day starts (`to`, exclusive), so the chosen day is included. */
export function dayEndOf(day: string): string | null {
  if (!DAY.test(day)) return null;
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().replace(".000Z", "Z");
}

/** The people the "Who" filter offers: everyone named on the rows read so far, as real or effective actor. */
export function peopleOf(rows: readonly AuditEntryRowView[]): readonly { readonly personId: string; readonly name: string }[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (row.realActorPersonId !== null && row.actorKind === "person" && !seen.has(row.realActorPersonId)) seen.set(row.realActorPersonId, row.actorName);
    if (row.effectiveActorPersonId !== null && row.effectiveActorName !== null && !seen.has(row.effectiveActorPersonId)) {
      seen.set(row.effectiveActorPersonId, row.effectiveActorName);
    }
  }
  return [...seen].map(([personId, name]) => ({ personId, name })).sort((a, b) => a.name.localeCompare(b.name, (portalLocale() === "el" ? "el-GR" : "en-GB")));
}

export type ActorTag = "operator" | "api_key" | "help" | null;

export interface ActorText {
  readonly name: string;
  readonly tag: ActorTag;
  /** Under the operator's name when they were acting as a member. */
  readonly onBehalfText: string | null;
}

export const ACTOR_TAG_TEXT: Readonly<Record<Exclude<ActorTag, null>, string>> = {
  get operator() { return portalText("finance", "m1191"); },
  get api_key() { return portalText("finance", "m1192"); },
  // plan-025 (design §36.16): a person's change confirmed on a wetalk help card.
  get help() { return portalText("finance", "audit.via_help"); },
};

export function actorTextOf(row: AuditEntryRowView): ActorText {
  if (row.actorKind === "operator") {
    // The operator's own name, as the entry recorded it (IC-13). The API sends it as `actor_name`
    // and, when they were acting as a member, as `impersonated_by_operator_name` too. AMENDED
    // plan-014 C4b: the member acted as is the entry's EFFECTIVE actor (0081) and is named when the
    // entry records them; an older entry that does not is still never given a guessed name.
    const acting = row.impersonatedByOperatorName !== null || row.effectiveActorName !== null;
    return {
      name: row.impersonatedByOperatorName ?? row.actorName,
      tag: "operator",
      onBehalfText: !acting ? null : row.effectiveActorName === null ? portalText("finance", "m1098") : portalText("finance", "m1099", { value1: row.effectiveActorName }),
    };
  }
  if (row.origin === "api_key") {
    // plan-014 C4b: a person-owned key acts FOR its owner (the effective actor).
    return { name: row.actorName, tag: "api_key", onBehalfText: row.effectiveActorName === null ? null : portalText("finance", "m1100", { value1: row.effectiveActorName }) };
  }
  return { name: row.actorName, tag: row.viaHelp === true ? "help" : null, onBehalfText: null };
}

/** What the export's ready note says the file holds. */
export function exportReadyText(
  rowCount: number,
  eventClass: AuditEventClassView | null,
  expiresAtText: string,
): string {
  const scope = eventClass === null ? "" : portalText("finance", "m1101", { value1: filterLabelOf(eventClass) });
  return (
    portalText("finance", "audit.export", { count: rowCount, value1: countWord(rowCount), value3: scope, value4: expiresAtText })
  );
}
